import { Dialog, DialogPanel, DialogTitle } from '@headlessui/react';
import { GoogleDriveFile } from '@server/modules/data-room/common/external-storage-integration/google-drive/google-drive';
import { FileWithPath, fromEvent } from 'file-selector';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { DropEvent, useDropzone } from 'react-dropzone';
import { useFilePicker } from 'use-file-picker';

import FileIcon from '@/assets/icons/file-upload.svg';
import FolderIcon from '@/assets/icons/folder-upload.svg';
import GenericUploadIcon from '@/assets/images/generic-upload-icon.svg';
import { FileValidationResult } from '@/common/types';
import { ACCEPTED_EXTENSIONS, ACCEPTED_TYPES, FILE_UPLOAD_ACCEPT_OBJECT } from '@/common/utils';
import { MAX_FILE_SIZE, MAX_FILE_SIZE_MB } from '@/constants';
import { useNewDiligenceWizard } from '@/contexts/create-new-diligence-wizard/utils';
import { MarveriButton } from '@/pages/home/common/MarveriButton';
import { DocumentSetUploaderList } from '@/pages/home/content/DocumentSetUploaderList';
import { trpcReact } from '@/utils/trpc';

const MAX_INT_32 = 2147483647;

/**
 * Integration
 */
const INTEGRATION_DISPLAY_NAMES: Record<string, string> = {
  'google-drive': 'Google Drive',
  // TODO: Add other integrations
  // 'onedrive': 'OneDrive',
  // 'sharepoint': 'SharePoint',
  // 'dropbox': 'Dropbox',
  // 'box': 'Box',
};

type IntegrationStatus =
  | 'select-integration'
  | 'integration-selected'
  | 'confirm-sharing'
  | 'import-files'
  | 'uploading'
  | 'upload-success'
  | 'upload-error';
interface DocumentSetUploaderProps {
  onUploadDone: (files: File[]) => void;
  onFileDelete: (fileToDelete: File) => void;
  totalCount: number;
}

export const DocumentSetUploader = ({
  onUploadDone,
  onFileDelete,
  totalCount,
}: DocumentSetUploaderProps) => {
  const MAX_FILES = 1000 - totalCount; // TODO: remove magic number

  const { newDiligenceData, updateNewDiligenceData } = useNewDiligenceWizard();

  const { dataRoomFiles } = newDiligenceData;

  const folderInputRef = useRef<HTMLInputElement | null>(null);
  const [dragging, setDragging] = useState(false);
  const [rejectedFiles, setRejectedFiles] = useState<FileValidationResult[]>([]);
  const [uploadedFiles, setUploadedFiles] = useState<FileWithPath[] | undefined>(dataRoomFiles);

  /**
   * Common Functions for file upload
   */
  const formatClientName = (date: Date) => {
    const options: Intl.DateTimeFormatOptions = {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    };

    return date.toLocaleDateString('en-US', options);
  };

  const validateFile = (file: FileWithPath): FileValidationResult => {
    if (file.size > MAX_FILE_SIZE) {
      return { file, valid: false, reason: `File too large (${MAX_FILE_SIZE_MB}MB limit)` };
    }
    if (!file.size) {
      return { file, valid: false, reason: 'Empty file - try unzipping before dragging' };
    }
    if (!ACCEPTED_TYPES.includes(file.type)) {
      return { file, valid: false, reason: 'Wrong file type' };
    }
    return { file, valid: true };
  };

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      const filesToUpload = [...(uploadedFiles || []), ...(acceptedFiles || [])];
      setDragging(false);
      setUploadedFiles((prevFiles) => {
        const files = Array.isArray(prevFiles) ? prevFiles : [];
        return [...files, ...acceptedFiles];
      });
      onUploadDone(acceptedFiles);
      const dateInMS = Date.now() % MAX_INT_32;
      updateNewDiligenceData({
        client: {
          name: formatClientName(new Date()),
          number: dateInMS,
        },
        name: formatClientName(new Date()),
        number: dateInMS,
        dataRoomFiles: filesToUpload,
        selectedTools: [...(newDiligenceData?.selectedTools || [])],
      });
    },
    [newDiligenceData?.selectedTools, onUploadDone, updateNewDiligenceData, uploadedFiles],
  );

  const getFilesOrFolders = async (event: DropEvent) => {
    const raw_files = await fromEvent(event);
    const files = raw_files.filter((file) => (file as FileWithPath).path !== undefined);
    const validatedFiles = files
      .slice(0, MAX_FILES * 10)
      .map((file) => validateFile(file as FileWithPath));

    const validFiles = validatedFiles.filter((result) => result.valid).map((result) => result.file);
    const rejectedFilesWithReasons = validatedFiles.filter((result) => !result.valid);

    setRejectedFiles((prevRejectedFiles) => [...prevRejectedFiles, ...rejectedFilesWithReasons]);
    return validFiles.slice(0, MAX_FILES);
  };

  const processFiles = useCallback(
    (files: FileWithPath[], folderFiles?: boolean) => {
      const validatedFiles = files.slice(0, MAX_FILES * 10).map((file) => validateFile(file));

      const validFiles = validatedFiles
        .filter((result) => result.valid)
        .map((result) => result.file);
      const rejectedFilesWithReasons = validatedFiles.filter((result) => !result.valid);
      setRejectedFiles((prevRejectedFiles) => [...prevRejectedFiles, ...rejectedFilesWithReasons]);

      const acceptedFiles = validFiles.slice(0, MAX_FILES);

      setUploadedFiles((prevFiles) => {
        const files = Array.isArray(prevFiles) ? prevFiles : [];
        return [...files, ...acceptedFiles];
      });

      const foldersWithFiles = Array.from(acceptedFiles || []).map((file) => {
        const fullPath = '/' + (file.webkitRelativePath || file.name);
        return new File([file], fullPath, { type: file.type });
      });

      const filesToUpload = [...(uploadedFiles || []), ...(acceptedFiles || [])];
      const foldersToUpload = [...(uploadedFiles || []), ...(foldersWithFiles || [])];

      onUploadDone(folderFiles ? foldersToUpload : filesToUpload);
      const dateInMS = Date.now() % MAX_INT_32;
      updateNewDiligenceData({
        client: {
          name: formatClientName(new Date()),
          number: dateInMS,
        },
        name: formatClientName(new Date()),
        number: dateInMS,
        dataRoomFiles: folderFiles ? foldersToUpload : filesToUpload,
        selectedTools: [...(newDiligenceData?.selectedTools || [])],
      });
    },
    [
      MAX_FILES,
      newDiligenceData?.selectedTools,
      onUploadDone,
      updateNewDiligenceData,
      uploadedFiles,
    ],
  );

  const { openFilePicker } = useFilePicker({
    readAs: 'DataURL',
    accept: ACCEPTED_TYPES,
    multiple: true,
    onFilesSuccessfullySelected: async ({ plainFiles }) => {
      const files = plainFiles.filter((file) => (file as FileWithPath).path !== undefined);
      processFiles(files);
    },
  });

  const handleFolderUploadClick = () => {
    folderInputRef.current?.click();
  };

  const onFolderUpload = useCallback(
    (selectedFolder: FileList | null) => {
      if (!selectedFolder) return;
      const files = Array.from(selectedFolder).filter(
        (file) => (file as FileWithPath).webkitRelativePath !== undefined,
      );

      processFiles(files, true);
    },
    [processFiles],
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    maxFiles: MAX_FILES,
    accept: FILE_UPLOAD_ACCEPT_OBJECT,
    getFilesFromEvent: (event) => getFilesOrFolders(event),
    noClick: true,
    noKeyboard: true,
    onDragLeave: () => setDragging(false),
  });

  const dragOverListener = useCallback(() => {
    setDragging(true);
  }, []);

  const fileFormatString = useMemo(() => {
    return ACCEPTED_EXTENSIONS.join(', ');
  }, []);

  /**
   * State variables for external storage integration
   */
  const { allowedIntegrations } = useFlags(); // TODO: Update Feature Flag
  const enableIntegration: boolean = Boolean(allowedIntegrations?.length);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const [currentStep, setCurrentStep] = useState<IntegrationStatus>('select-integration');
  const [selectedIntegration, setSelectedIntegration] = useState<string | null>(null);
  const [serviceAccountEmail, setServiceAccountEmail] = useState<string | null>(null); // TODO: Update to integration-selected / confirm-sharing

  const [files, setFiles] = useState<GoogleDriveFile[]>([]);
  const [loading, setLoading] = useState(false);

  /**
   * Common functions for external storage integration
   */
  const openModal = () => {
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
  };

  const goBackToStepIntegrationSelection = () => {
    setCurrentStep('select-integration');
    setSelectedIntegration(null);
    setServiceAccountEmail(null);
  };

  const confirmSharing = () => {
    setCurrentStep('confirm-sharing');
  };

  const handleIntegrationSelect = async (integration: string) => {
    setSelectedIntegration(integration);
    setCurrentStep('integration-selected');

    if (integration === 'google-drive') {
      try {
        const { mutateAsync, error } = trpcReact.dataRoom.createGoogleServiceAccount.useMutation(); // TODO: consider adding isLoading
        const result = await mutateAsync();
        if (error) {
          // eslint-disable-next-line no-console
          console.error('Error creating Google Service Account:', error);
          throw error;
        }
        if (result) {
          setServiceAccountEmail(result.email);
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error creating Google Service Account:', error);
      }
    }
  };

  /**
   * Google Drive Integration
   */
  // Fetch Google Drive Files
  const fetchGoogleDriveFiles = useCallback(async () => {
    setLoading(true);
    try {
      const { data, isLoading, error } = trpcReact.dataRoom.listGoogleDriveFiles.useQuery();
      // TODO: Currently for "one time upload" ignore refetch logic
      // consider using below for refetch logic for "continuous sync"
      // const listGoogleDriveFilesQuery = trpcReact.dataRoom.listGoogleDriveFiles.useQuery(undefined, {
      //   enabled: false, // Set to true for "continuous sync"
      // });
      // const result = await listGoogleDriveFilesQuery.refetch();

      if (error) {
        // eslint-disable-next-line no-console
        console.error('Error fetching Google Drive files:', error);
        throw error;
      }

      if (!isLoading && data) {
        // Ensure the data matches the GoogleDriveFile interface
        const typedFileData = data.map((file) => ({
          id: file.id ?? '',
          name: file.name ?? '',
          mimeType: file.mimeType ?? '',
          size: file.size ?? undefined,
          webContentLink: file.webContentLink ?? undefined,
          webViewLink: file.webViewLink ?? undefined,
          modifiedTime: file.modifiedTime ?? undefined,
        }));
        setFiles(typedFileData);
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error connecting to Google Drive:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (currentStep === 'import-files') {
      fetchGoogleDriveFiles();
    }
  }, [currentStep, fetchGoogleDriveFiles]);

  // Import & Upload Google Drive Files
  const importGoogleDriveFiles = trpcReact.dataRoom.importGoogleDriveFiles.useMutation();

  const handleFinalConfirmation = async () => {
    // Logic for final confirmation, e.g., closing the modal
    const dateInMS = Date.now() % MAX_INT_32;
    await importGoogleDriveFiles.mutateAsync({
      files: [files[0]].map((file) => ({
        id: file.id ?? '',
        mimeType: file.mimeType ?? '',
      })),
      clientNumber: dateInMS,
      matterNumber: dateInMS,
      clientName: formatClientName(new Date()),
      matterName: formatClientName(new Date()),
      tools: [],
    });
    closeModal();
  };

  // NOTE: UI for integration is not complete
  // TODO: Remove placeholder UI and implement proper UI UX for integration
  return (
    <div className="relative mb-[24px] flex w-full flex-col" data-testid="dataroom-uploader">
      <div className="flex items-baseline justify-between">
        <h1 className="text-[20px] font-medium leading-[30px] text-marveri-white">Upload Files</h1>
        <div>
          {uploadedFiles === undefined ||
          (uploadedFiles.length === 0 && rejectedFiles.length === 0) ? (
            <div
              className="text-center text-[14px] font-medium text-marveri-light-silver"
              data-testid="dataroom-uploader-no-docs"
            >
              No files uploaded
            </div>
          ) : (
            <div className="flex">
              <h1 className="font-medium text-[#868686]">{`${uploadedFiles?.length} File${
                uploadedFiles?.length > 1 ? `s` : ''
              } Successfully Uploaded`}</h1>
              {rejectedFiles && rejectedFiles.length !== 0 && (
                <>
                  <span className="px-[5px] font-medium text-marveri-white">{`/`}</span>
                  <h1 className="font-medium text-marveri-red">{`${rejectedFiles.length} File${
                    rejectedFiles.length > 1 ? 's' : ''
                  } Rejected`}</h1>
                </>
              )}
            </div>
          )}
        </div>
      </div>
      <div className="flex w-full">
        <div className="flex w-full gap-2 pt-[16px] ">
          {totalCount + rejectedFiles.length === 0 || isDragActive || dragging ? (
            <div
              {...getRootProps()}
              className={`relative flex h-[264px] w-full cursor-not-allowed flex-col items-center justify-center rounded-lg border border-dashed border-dark-border shadow small:h-[180px] ${
                isDragActive ? 'border-blue-500 bg-container-selected' : 'bg-[#19181A] '
              }`}
            >
              <form>
                <input {...getInputProps()} />
              </form>
              <div className="flex flex-col items-center justify-center py-[10px]">
                <div className="flex flex-col items-center text-center">
                  <img src={GenericUploadIcon} alt="icon" />
                  <p className="p-2 pb-[7px] text-[18px] font-bold text-marveri-white">
                    Drag & Drop Files or Folders
                  </p>
                  <div>
                    <div>
                      <p className="text-[12px] font-normal text-N400">
                        Supported file formats: <br />{' '}
                        <span className="font-semibold">{fileFormatString}</span>
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div onDragOver={dragOverListener} className="w-full">
              <DocumentSetUploaderList
                onFileDelete={onFileDelete}
                uploadedFiles={uploadedFiles}
                setUploadedFiles={setUploadedFiles}
                rejectedFiles={rejectedFiles}
                setRejectedFiles={setRejectedFiles}
              />
            </div>
          )}
          <div className="flex h-[264px] flex-col justify-evenly gap-2 small:h-[180px]">
            <div
              className={`flex w-[160px] cursor-pointer flex-col items-center justify-center gap-2 rounded-lg border border-dashed border-dark-border bg-[#19181A] shadow hover:border-solid hover:bg-container-selected small:h-[180px] ${
                !enableIntegration ? 'h-1/2' : 'h-1/3'
              }`}
              onClick={openFilePicker}
            >
              <img src={FileIcon} alt="file-icon" />
              <h1>File Upload</h1>
            </div>
            <div
              className={`flex w-[160px] cursor-pointer flex-col items-center justify-center gap-2 rounded-lg border border-dashed border-dark-border bg-[#19181A] shadow hover:border-solid hover:bg-container-selected small:h-[180px] ${
                !enableIntegration ? 'h-1/2' : 'h-1/3'
              }`}
              onClick={handleFolderUploadClick}
            >
              <img src={FolderIcon} alt="folder-icon" />
              <h1>Folder Upload</h1>
            </div>
            {enableIntegration && (
              <div
                className="flex h-1/3 w-[160px] cursor-pointer flex-col items-center justify-center gap-2 rounded-lg border border-dashed border-dark-border bg-[#19181A] shadow hover:border-solid hover:bg-container-selected small:h-[180px]"
                onClick={openModal}
              >
                <img src={FolderIcon} alt="folder-icon" />
                <h1>Integrate External Storage</h1>
              </div>
            )}
            <input
              type="file"
              id="folderInput"
              ref={folderInputRef}
              style={{ display: 'none' }}
              multiple
              onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                onFolderUpload(event.target.files)
              }
              {...({ webkitdirectory: 'true' } as { webkitdirectory: string })}
            />
          </div>
        </div>
      </div>
      {isModalOpen && (
        <Dialog
          open={isModalOpen}
          onClose={closeModal}
          className="fixed inset-0 z-10 flex items-center justify-center overflow-y-auto bg-black/50"
        >
          <DialogPanel className="flex w-[554px] flex-col items-center justify-center rounded-[5px] border-2 border-dark-border bg-container-dark p-4 text-marveri-white">
            {currentStep === 'select-integration' ? (
              <>
                <DialogTitle className="text-lg font-medium leading-6">
                  Select external storage to integrate
                </DialogTitle>
                <div className="mt-4 flex flex-col gap-3">
                  {allowedIntegrations.map((integration: string) => (
                    <MarveriButton
                      key={integration}
                      disabled={false}
                      onClick={() => handleIntegrationSelect(integration)}
                      className="w-full justify-start px-4 py-3 text-left"
                    >
                      <div className="flex items-center gap-3">
                        <img
                          src={`/assets/icons/${integration}.svg`}
                          alt={`${INTEGRATION_DISPLAY_NAMES[integration]} icon`}
                          className="size-6"
                        />
                        <span>{INTEGRATION_DISPLAY_NAMES[integration]}</span>
                      </div>
                    </MarveriButton>
                  ))}
                </div>
              </>
            ) : currentStep === 'integration-selected' ? (
              <>
                <DialogTitle className="text-center text-lg font-medium leading-6">
                  Instructions for {INTEGRATION_DISPLAY_NAMES[selectedIntegration ?? '']}
                </DialogTitle>
                <div className="mt-6 flex flex-col items-center">
                  {serviceAccountEmail ? (
                    <>
                      <div className="space-y-4 text-center">
                        <div className="rounded-lg p-4">
                          <p className="font-medium">Service Account Email</p>
                          <p className="break-all text-sm">{serviceAccountEmail}</p>
                        </div>
                        <p className="">
                          Please copy this email address and share your files with it.
                        </p>
                      </div>
                    </>
                  ) : (
                    <div className="flex items-center space-x-2 ">
                      <p>Loading service account details...</p>
                    </div>
                  )}
                </div>
                <div className="mt-8 flex justify-between">
                  <MarveriButton
                    disabled={false}
                    onClick={goBackToStepIntegrationSelection}
                    className="px-6"
                  >
                    Back
                  </MarveriButton>
                  {serviceAccountEmail && (
                    <MarveriButton disabled={false} onClick={confirmSharing} className="px-6">
                      Confirm Sharing
                    </MarveriButton>
                  )}
                </div>
              </>
            ) : currentStep === 'import-files' ? (
              <>
                <DialogTitle className="text-lg font-medium leading-6">
                  Confirm Google Drive Files
                </DialogTitle>
                <div className="mt-4">
                  {loading ? (
                    <p>Loading files...</p>
                  ) : (
                    <ul>
                      {files.map((file, index) => (
                        <li key={index} className="mb-2">
                          <p>Name: {file.name}</p>
                          <p>Mimetype: {file.mimeType}</p>
                          <p>Size: {file.size}</p>
                        </li>
                      ))}
                    </ul>
                  )}
                  <button
                    type="button"
                    className="mt-4 inline-flex justify-center rounded-md border border-transparent px-4 py-2 text-sm font-medium text-white shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                    onClick={handleFinalConfirmation}
                  >
                    Confirm and Close
                  </button>
                </div>
              </>
            ) : (
              <div>
                <p>Uploading files...</p>
              </div>
            )}
            <button
              type="button"
              className="mt-4 inline-flex justify-center rounded-md border border-transparent px-4 py-2 text-sm font-medium text-white shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
              onClick={closeModal}
            >
              Close
            </button>
          </DialogPanel>
        </Dialog>
      )}
    </div>
  );
};
